import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId } from '@/lib/format';
import { Account, AccountType, Transfer } from '@/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Plus, X, ArrowLeftRight, Wallet, CreditCard, Landmark, Banknote, HelpCircle, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';


const ACCOUNT_TYPES: { value: AccountType; label: string; icon: typeof Wallet }[] = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'transaction', label: 'Transaction', icon: Wallet },
  { value: 'savings', label: 'Savings', icon: Landmark },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'other', label: 'Other', icon: HelpCircle },
];

function getAccountIcon(type: AccountType) {
  return ACCOUNT_TYPES.find(t => t.value === type)?.icon ?? Wallet;
}

type View = 'list' | 'add' | 'transfer' | 'detail';

export default function AccountsPage() {
  const { data, updateData } = useAuth();
  const [view, setView] = useState<View>('list');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Add account form
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('transaction');
  const [initialBalance, setInitialBalance] = useState('');

  // Transfer form
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDesc, setTransferDesc] = useState('');

  const accounts = data.accounts ?? [];
  const transfers = data.transfers ?? [];

  const accountBalances = useMemo(() => {
    const balances: Record<string, number> = {};
    for (const acc of accounts) {
      balances[acc.id] = acc.initialBalance;
    }
    for (const t of data.transactions) {
      if (t.accountId && balances[t.accountId] !== undefined) {
        balances[t.accountId] += t.type === 'income' ? t.amount : -t.amount;
      }
    }
    for (const tr of transfers) {
      if (balances[tr.fromAccountId] !== undefined) balances[tr.fromAccountId] -= tr.amount;
      if (balances[tr.toAccountId] !== undefined) balances[tr.toAccountId] += tr.amount;
    }
    return balances;
  }, [accounts, data.transactions, transfers]);

  const netWorth = useMemo(() => {
    return Object.values(accountBalances).reduce((s, b) => s + b, 0);
  }, [accountBalances]);

  const resetAddForm = () => { setName(''); setAccountType('transaction'); setInitialBalance(''); };

  const handleAddAccount = async () => {
    if (!name.trim()) { toast.error('Enter an account name'); return; }
    const bal = parseFloat(initialBalance || '0');
    if (isNaN(bal)) { toast.error('Enter a valid balance'); return; }
    await updateData(prev => ({
      ...prev,
      accounts: [...prev.accounts, { id: generateId(), name: name.trim(), type: accountType, initialBalance: bal, createdAt: new Date().toISOString() }],
    }));
    toast.success('Account added');
    resetAddForm();
    setView('list');
  };

  const handleTransfer = async () => {
    if (!fromAccountId || !toAccountId || fromAccountId === toAccountId) {
      toast.error('Select two different accounts');
      return;
    }
    const amt = parseFloat(transferAmount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    const transfer: Transfer = {
      id: generateId(),
      fromAccountId,
      toAccountId,
      amount: amt,
      date: new Date().toISOString().slice(0, 10),
      description: transferDesc.trim(),
      createdAt: new Date().toISOString(),
    };
    await updateData(prev => ({
      ...prev,
      transfers: [...(prev.transfers ?? []), transfer],
    }));
    const fromName = accounts.find(a => a.id === fromAccountId)?.name;
    const toName = accounts.find(a => a.id === toAccountId)?.name;
    toast.success(`${formatAUD(amt)} transferred`, { description: `${fromName} → ${toName}` });
    setTransferAmount(''); setTransferDesc(''); setFromAccountId(''); setToAccountId('');
    setView('list');
  };

  const deleteAccount = async (id: string) => {
    const hasTxns = data.transactions.some(t => t.accountId === id);
    const hasTransfers = transfers.some(t => t.fromAccountId === id || t.toAccountId === id);
    if (hasTxns || hasTransfers) {
      toast.error('Cannot delete account with transactions or transfers');
      return;
    }
    await updateData(prev => ({ ...prev, accounts: prev.accounts.filter(a => a.id !== id) }));
    toast.success('Account deleted');
    setView('list');
  };

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  // Detail view: transactions for this account
  const accountTransactions = useMemo(() => {
    if (!selectedAccountId) return [];
    return data.transactions
      .filter(t => t.accountId === selectedAccountId)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [data.transactions, selectedAccountId]);

  const accountTransfers = useMemo(() => {
    if (!selectedAccountId) return [];
    return transfers
      .filter(t => t.fromAccountId === selectedAccountId || t.toAccountId === selectedAccountId)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [transfers, selectedAccountId]);

  if (view === 'detail' && selectedAccount) {
    const Icon = getAccountIcon(selectedAccount.type);
    const balance = accountBalances[selectedAccount.id] ?? 0;
    return (
      <div className="px-4 pt-6 pb-4">
        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="font-display text-xl font-bold">{selectedAccount.name}</h1>
        </div>
        <Card className="mb-4">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground capitalize">{selectedAccount.type.replace('_', ' ')}</p>
              <p className={cn('font-display text-xl font-bold', balance < 0 && 'text-destructive')}>{formatAUD(balance)}</p>
            </div>
          </CardContent>
        </Card>

        <h2 className="mb-2 font-display text-lg font-semibold">Transactions</h2>
        {accountTransactions.length === 0 && accountTransfers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No transactions for this account</p>
        ) : (
          <div className="space-y-2">
            {accountTransactions.map(t => (
              <Card key={t.id}>
                <CardContent className="flex items-center justify-between p-3">
                  <div>
                    <p className="font-medium text-sm">{t.description.replace(/^\[basiq:[^\]]+\]\s*/, '') || t.category}</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.date).toLocaleDateString('en-AU')}</p>
                  </div>
                  <p className={cn('font-display font-semibold', t.type === 'income' ? 'text-success' : 'text-destructive')}>
                    {t.type === 'income' ? '+' : '-'}{formatAUD(t.amount)}
                  </p>
                </CardContent>
              </Card>
            ))}
            {accountTransfers.map(tr => {
              const isFrom = tr.fromAccountId === selectedAccountId;
              const otherAcc = accounts.find(a => a.id === (isFrom ? tr.toAccountId : tr.fromAccountId));
              return (
                <Card key={tr.id}>
                  <CardContent className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-1">
                        <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" />
                        {isFrom ? `To ${otherAcc?.name ?? 'Unknown'}` : `From ${otherAcc?.name ?? 'Unknown'}`}
                      </p>
                      <p className="text-xs text-muted-foreground">{new Date(tr.date).toLocaleDateString('en-AU')}{tr.description && ` · ${tr.description}`}</p>
                    </div>
                    <p className={cn('font-display font-semibold', isFrom ? 'text-destructive' : 'text-success')}>
                      {isFrom ? '-' : '+'}{formatAUD(tr.amount)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Button variant="destructive" size="sm" className="mt-6 gap-1.5" onClick={() => deleteAccount(selectedAccount.id)}>
          <Trash2 className="h-4 w-4" /> Delete Account
        </Button>
      </div>
    );
  }

  if (view === 'add') {
    return (
      <div className="px-4 pt-6 pb-4">
        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => { resetAddForm(); setView('list'); }}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="font-display text-xl font-bold">Add Account</h1>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Account Name</Label>
            <Input placeholder="e.g. Everyday Account" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={accountType} onValueChange={v => setAccountType(v as AccountType)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Current Balance (AUD)</Label>
            <Input type="number" step="0.01" placeholder="0.00" value={initialBalance} onChange={e => setInitialBalance(e.target.value)} className="mt-1" />
          </div>
          <Button onClick={handleAddAccount} className="w-full">Add Account</Button>
        </div>
      </div>
    );
  }

  if (view === 'transfer') {
    return (
      <div className="px-4 pt-6 pb-4">
        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="font-display text-xl font-bold">Transfer</h1>
        </div>
        {accounts.length < 2 ? (
          <p className="text-muted-foreground">You need at least two accounts to make a transfer.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>From</Label>
              <Select value={fromAccountId} onValueChange={setFromAccountId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({formatAUD(accountBalances[a.id] ?? 0)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Select value={toAccountId} onValueChange={setToAccountId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({formatAUD(accountBalances[a.id] ?? 0)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (AUD)</Label>
              <Input type="number" step="0.01" min="0" placeholder="0.00" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input placeholder="What's this transfer for?" value={transferDesc} onChange={e => setTransferDesc(e.target.value)} className="mt-1" />
            </div>
            <Button onClick={handleTransfer} className="w-full">Transfer</Button>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="px-4 pt-6 pb-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Accounts</h1>
        <div className="flex gap-2">
          {accounts.length >= 2 && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setView('transfer')}>
              <ArrowLeftRight className="h-4 w-4" /> Transfer
            </Button>
          )}
          <Button size="sm" className="gap-1.5" onClick={() => setView('add')}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>


      {/* Net Worth */}
      {accounts.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Net Worth</p>
            <p className={cn('font-display text-2xl font-bold', netWorth < 0 && 'text-destructive')}>{formatAUD(netWorth)}</p>
          </CardContent>
        </Card>
      )}

      {accounts.length === 0 ? (
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">No accounts yet</p>
          <p className="text-sm text-muted-foreground">Add your cash, bank, or credit card accounts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map(acc => {
            const Icon = getAccountIcon(acc.type);
            const balance = accountBalances[acc.id] ?? 0;
            return (
              <Card key={acc.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => { setSelectedAccountId(acc.id); setView('detail'); }}>
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{acc.name}</p>
                    <Badge variant="secondary" className="text-[10px] capitalize">{acc.type.replace('_', ' ')}</Badge>
                  </div>
                  <p className={cn('font-display font-semibold', balance < 0 && 'text-destructive')}>{formatAUD(balance)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
